import React, { useState, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { chatService, feedbackService, llmConfigService, processService, projectService, resolveGoogleDocsAuthUrl } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChatHistorySidebar from './ChatHistorySidebar';
import CodeAnalysisForm from './CodeAnalysisForm';
import ProcessSelector from '../ProcessSelector';
import GoogleDocsExportModal from '../GoogleDocsExportModal';
import FeedbackModal from '../FeedbackModal';
import ThoughtChain from '../ThoughtChain';
import useConsoleErrors from '../../hooks/useConsoleErrors';
import { useAuth } from '../../context/AuthContext';

const ChatTab = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [streamingStatus, setStreamingStatus] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [sliderPresets, setSliderPresets] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [currentThinkingLevel, setCurrentThinkingLevel] = useState('low');
  const [sliderValue, setSliderValue] = useState(2);
  const [isCustomSelection, setIsCustomSelection] = useState(false);
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [exportingMessageId, setExportingMessageId] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportedDocument, setExportedDocument] = useState({ url: '', title: '' });
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [projectName, setProjectName] = useState(null);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [showGoogleDocsPermissionModal, setShowGoogleDocsPermissionModal] = useState(false);
  const [googleDocsAuthUrl, setGoogleDocsAuthUrl] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState({ messageId: null, rating: null, initialType: 'BUG' });
  const [ratedMessages, setRatedMessages] = useState({});
  const chatContainerRef = useRef(null);
  const activeStreamControllerRef = useRef(null);
  const activeStreamTypeRef = useRef(null);
  const { getRecentErrors } = useConsoleErrors();
  const { user } = useAuth();

  const isAbortLikeError = (error) => {
    if (!error) {
      return false;
    }

    return error.name === 'AbortError'
      || error.code === 'REQUEST_ABORTED'
      || error.code === 'ERR_CANCELED';
  };

  const stopActiveResponse = () => {
    if (activeStreamControllerRef.current && !activeStreamControllerRef.current.signal.aborted) {
      activeStreamControllerRef.current.abort();
    }
  };

  const normalizeThinkingLevel = (value) => {
    if (!value || typeof value !== 'string') {
      return 'low';
    }
    const normalized = value.toLowerCase();
    return ['minimal', 'low', 'medium', 'high'].includes(normalized) ? normalized : 'low';
  };

  const buildDefaultSliderPresets = (models) => {
    const firstModel = models[0]?.modelName || null;
    const minimalModel = models.find((model) => model.modelName === 'gemini-3-flash-preview')?.modelName;

    return [1, 2, 3].map((position) => ({
      sliderPosition: position,
      modelName: position === 1 && minimalModel ? minimalModel : firstModel,
      thinkingLevel: position === 1 && minimalModel ? 'minimal' : 'low',
      isActive: true
    }));
  };

  const mergeSliderPresets = (models, presets) => {
    const defaults = buildDefaultSliderPresets(models);
    if (!presets || presets.length === 0) {
      return defaults;
    }

    const byPosition = new Map(presets.map((preset) => [preset.sliderPosition, preset]));
    return defaults.map((fallbackPreset) => {
      const existing = byPosition.get(fallbackPreset.sliderPosition);
      if (!existing) {
        return fallbackPreset;
      }
      return {
        ...existing,
        thinkingLevel: normalizeThinkingLevel(existing.thinkingLevel),
        modelName: existing.modelName || fallbackPreset.modelName,
        isActive: existing.isActive !== false
      };
    });
  };

  const isMinimalSupported = (modelName) => modelName === 'gemini-3-flash-preview';

  const buildModelSelection = (models, modelName) => {
    if (!models || models.length === 0) {
      return null;
    }
    const selected = models.find((model) => model.modelName === modelName) || models[0];
    return {
      modelName: selected.modelName,
      displayName: selected.displayName,
      provider: selected.provider
    };
  };

  const applySliderPreset = (nextSliderValue, presets, models) => {
    const preset = presets.find((entry) => entry.sliderPosition === nextSliderValue && entry.isActive !== false)
      || presets.find((entry) => entry.sliderPosition === nextSliderValue)
      || presets[0];
    if (!preset) {
      return;
    }

    const normalizedThinking = normalizeThinkingLevel(preset.thinkingLevel);
    const safeThinking = normalizedThinking === 'minimal' && !isMinimalSupported(preset.modelName)
      ? 'low'
      : normalizedThinking;

    const selectedModel = buildModelSelection(models, preset.modelName);
    setSliderValue(nextSliderValue);
    setIsCustomSelection(false);
    setCurrentModel(selectedModel);
    setCurrentThinkingLevel(safeThinking);
  };

  const toolLabelMap = {
    analysis_phase_preparation: 'Preparation',
    analysis_phase_review: 'Independent code reviews',
    analysis_phase_consolidation: 'Consolidation and verification',
    analysis_conversation: 'Preparing conversation',
    analysis_load_files: 'Loading files for analysis',
    analysis_select_mode: 'Selecting analysis mode',
    analysis_load_context: 'Loading process context',
    analysis_select_model: 'Selecting model',
    analysis_select_models: 'Selecting reviewer models',
    analysis_record_request: 'Recording request',
    analysis_build_context: 'Preparing code context',
    analysis_load_prompt: 'Loading analysis instructions',
    analysis_prompt_cache: 'Checking prompt cache',
    analysis_single_pass: 'Running code analysis',
    analysis_reviewer_a: 'Reviewer A analysis',
    analysis_reviewer_b: 'Reviewer B analysis',
    analysis_create_cache: 'Creating shared reviewer cache',
    analysis_consolidation_cache: 'Creating consolidation cache',
    analysis_persist_response: 'Saving analysis response',
    retrieve_uipath_code: 'Searching UiPath code',
    retrieve_all_project_code: 'Reviewing full project code',
    retrieve_specific_workflow: 'Retrieving workflow',
    get_project_file_list: 'Listing project files',
    retrieve_activities_by_keyword: 'Searching activities',
    query_uipath_code: 'Querying code',
    knowledge_search: 'Searching code knowledge',
    code_search: 'Searching code',
    search_code: 'Searching code',
    query_uipath_code_sql: 'Querying code',
    uipath_workflow_analyzer: 'Retrieving workflow analyzer results',
    retrieve_company_guidelines: 'Searching guidelines',
    google_search: 'Searching the web'
  };

  const getToolLabel = (toolName, status) => {
    if (status === 'tool_end') {
      return 'Thinking';
    }
    if (status === 'tool_error') {
      const baseLabel = toolLabelMap[toolName] || toolName;
      return `Failed: ${baseLabel}`;
    }
    const baseLabel = toolLabelMap[toolName] || `Running ${toolName}`;
    return baseLabel;
  };

  const isThinkingStep = (step) => !step?.tool || step.tool === 'thinking';

  const isToolCallStep = (step) => {
    if (step?.level === 'primary') return false; // Primary-level steps are never indented
    return !isThinkingStep(step) || step?.level === 'sub';
  };

  const primaryTools = new Set([
    'analysis_phase_preparation',
    'analysis_phase_review',
    'analysis_phase_consolidation'
  ]);

  const preparationTools = new Set([
    'analysis_phase_preparation',
    'analysis_conversation',
    'analysis_load_files',
    'analysis_select_mode',
    'analysis_load_context',
    'analysis_select_model',
    'analysis_select_models',
    'analysis_record_request',
    'analysis_build_context',
    'analysis_load_prompt',
    'analysis_prompt_cache'
  ]);

  const normalizeAnalysisTool = (tool) => {
    if (!tool) {
      return tool;
    }
    if (preparationTools.has(tool)) {
      return 'analysis_phase_preparation';
    }
    return tool;
  };

  const normalizeAnalysisThoughtChain = (steps = []) => {
    if (!Array.isArray(steps) || steps.length === 0) {
      return [];
    }

    const normalized = [];
    let preparationStepIndex = -1;

    steps.forEach((rawStep) => {
      if (!rawStep) {
        return;
      }

      const tool = normalizeAnalysisTool(rawStep.tool);
      const step = {
        ...rawStep,
        tool,
        label: tool === 'analysis_phase_preparation'
          ? 'Preparation'
          : (rawStep.label || (tool ? getToolLabel(tool, rawStep.status) : 'Thinking')),
        level: rawStep.level || (isThinkingStep({ tool }) ? 'primary' : (primaryTools.has(tool) ? 'primary' : 'sub'))
      };

      if (tool === 'analysis_phase_preparation' || tool === 'analysis_phase_review') {
        step.level = 'primary';
      }

      if (tool !== 'analysis_phase_preparation') {
        normalized.push(step);
        return;
      }

      if (preparationStepIndex === -1) {
        preparationStepIndex = normalized.length;
        normalized.push(step);
        return;
      }

      const existing = normalized[preparationStepIndex];
      normalized[preparationStepIndex] = {
        ...existing,
        status: step.status === 'failed' ? 'failed' : (step.status === 'running' ? 'running' : existing.status),
        detail: step.detail || existing.detail,
        fullDetail: step.fullDetail || existing.fullDetail,
        durationMs: step.durationMs || existing.durationMs,
        startedAt: existing.startedAt || step.startedAt,
        level: 'primary'
      };
    });

    // Relabel thinking steps: keep their assigned labels but ensure sub-level
    // for thinking steps that appear during consolidation (after both phases exist)
    const hasConsolidationPhase = normalized.some(step => step.tool === 'analysis_phase_consolidation');
    const relabeled = normalized.map((step) => {
      if (step.tool !== 'thinking') {
        return step;
      }
      // Thinking steps with phase labels stay primary
      if (step.label === 'Preparation' || step.label === 'Independent code reviews') {
        return { ...step, level: 'primary' };
      }
      // Thinking steps during consolidation are sub-level
      return { ...step, level: hasConsolidationPhase ? 'sub' : step.level };
    });

    const phaseLabels = new Set(['Preparation', 'Independent code reviews']);
    const deduped = [];

    relabeled.forEach((step) => {
      if (!step || !phaseLabels.has(step.label)) {
        deduped.push(step);
        return;
      }

      const existingIndex = deduped.findIndex((entry) => entry?.label === step.label);
      if (existingIndex === -1) {
        deduped.push({ ...step, level: 'primary' });
        return;
      }

      const existing = deduped[existingIndex];
      const mergedStatus = existing.status === 'running' || step.status === 'running'
        ? 'running'
        : (existing.status === 'failed' || step.status === 'failed' ? 'failed' : (step.status || existing.status));

      deduped[existingIndex] = {
        ...existing,
        status: mergedStatus,
        detail: step.detail || existing.detail,
        fullDetail: step.fullDetail || existing.fullDetail,
        durationMs: step.durationMs || existing.durationMs,
        startedAt: existing.startedAt || step.startedAt,
        level: 'primary'
      };
    });

    return deduped;
  };

  // Load conversations and processes on mount or when projectId changes
  useEffect(() => {
    if (projectId) {
      // Reset state when switching projects
      setCurrentConversationId(null);
      setMessages([]);
      setError(null);
      setMetadata(null);
      setSelectedProcessId(null);
      setStreamingStatus(null);
      setThinkingSteps([]);
      setIsTyping(false);

      // Reset slider to default position 2 immediately; loadModelSettings will
      // overwrite with the DB-backed preset once the fetch completes.
      setSliderValue(2);
      setIsCustomSelection(false);
      
      loadConversations();
      loadModelSettings();
      loadProcesses();
      loadProjectName();
    } else {
      setIsLoadingConversations(false);
      setIsLoading(false);
    }
  }, [projectId]);

  // Listen for process changes from Project Configuration tab
  useEffect(() => {
    const handleProcessesChanged = (event) => {
      // Only reload if the event is for the current project
      if (event.detail.projectId === projectId) {
        console.log('🔄 Processes changed, reloading...');
        loadProcesses();
      }
    };

    window.addEventListener('processesChanged', handleProcessesChanged);

    return () => {
      window.removeEventListener('processesChanged', handleProcessesChanged);
    };
  }, [projectId]);

  // Load project name
  const loadProjectName = async () => {
    if (!projectId) return;
    
    try {
      const response = await projectService.getProject(projectId);
      if (response.data && response.data.name) {
        setProjectName(response.data.name);
      }
    } catch (err) {
      console.error('Error loading project name:', err);
      // Don't show error to user, just log it
    }
  };

  // Load processes for the project
  const loadProcesses = async () => {
    if (!projectId) return;
    
    setIsLoadingProcesses(true);
    try {
      const response = await processService.getProcessesByProject(projectId);
      const processesData = response.data || [];
      console.log('📋 Loaded processes:', processesData.length, processesData.map(p => ({ id: p.id, name: p.name })));
      setProcesses(processesData);
      
      // If a process was selected but no longer exists, clear selection
      if (selectedProcessId && !processesData.find(p => p.id === selectedProcessId)) {
        console.log('⚠️ Selected process no longer exists, clearing selection');
        setSelectedProcessId(null);
      }
      
      // If there's only one process left and it was selected, clear selection
      if (processesData.length <= 1 && selectedProcessId) {
        console.log('⚠️ Only 1 or fewer processes, clearing selection');
        setSelectedProcessId(null);
      }
    } catch (err) {
      console.error('Error loading processes:', err);
      setProcesses([]);
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  // Handle process selection change
  const handleProcessChange = (newProcessId) => {
    console.log('🔄 Process selection changed:', { from: selectedProcessId, to: newProcessId });
    setSelectedProcessId(newProcessId);
    // Optionally clear current conversation when changing scope
    // setCurrentConversationId(null);
    // setMessages([]);
  };

  // Load conversations list
  const loadConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const response = await chatService.getConversations(projectId, false);
      const convs = response.data || [];
      setConversations(convs);
      
      // Always show new chat state when switching projects
      setMessages([]);
      setCurrentConversationId(null);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setIsLoading(false);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (conversationId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await chatService.getConversationMessages(conversationId);
      setMessages(response.data || []);
      setCurrentConversationId(conversationId);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new conversation
  const handleNewChat = async () => {
    setCurrentConversationId(null);
    setMessages([]);
    setMetadata(null);
    setError(null);
    setShowAnalysisForm(false);
    setSliderValue(2);
    setIsCustomSelection(false);

    // Always use center slider preset defaults for new chats
    if (sliderPresets.length > 0 && availableModels.length > 0) {
      applySliderPreset(2, sliderPresets, availableModels);
    }
  };

  // Load available models and slider presets
  const loadModelSettings = async () => {
    try {
      const [modelsRes, presetsRes] = await Promise.all([
        llmConfigService.getAvailableModels(),
        llmConfigService.getSliderPresets()
      ]);

      const models = modelsRes.data || [];
      const mergedPresets = mergeSliderPresets(models, presetsRes.data || []);

      setAvailableModels(models);
      setSliderPresets(mergedPresets);

      // Ignore stored model preference and always initialize from center slider preset
      applySliderPreset(2, mergedPresets, models);
    } catch (err) {
      console.error('Error loading model settings:', err);
      // Set defaults on error - non-critical feature
      setCurrentModel({
        modelName: 'gemini-2.5-pro',
        displayName: 'Gemini 2.5 Pro',
        provider: 'vertex'
      });
      setCurrentThinkingLevel('low');
      setSliderValue(2);
      setIsCustomSelection(false);
      setSliderPresets([]);
      setAvailableModels([]);
    }
  };

  // Auto-scroll to bottom when new messages are added, typing indicator shows, or loading finishes
  useEffect(() => {
    if (chatContainerRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages, isTyping, isLoading]);

  const getRecentMessageSnippets = () => {
    const recent = messages.slice(-7);
    return recent.map((msg, idx) => {
      const text = String(msg.message || msg.text || '');
      const isLatestTwo = idx >= recent.length - 2;
      return {
        id: msg.id,
        sender: msg.sender,
        text: isLatestTwo ? text : text.slice(0, 200),
        ...(isLatestTwo && msg.llmExecution ? { llmExecution: msg.llmExecution } : {})
      };
    });
  };

  const buildFeedbackContext = (messageId) => {
    // Find the target message and include its full content + llmExecution
    const targetMsg = messages.find((m) => m.id === messageId);
    const targetContext = targetMsg
      ? {
          targetMessageContent: String(targetMsg.message || targetMsg.text || ''),
          targetMessageSender: targetMsg.sender,
          targetLlmExecution: targetMsg.llmExecution || null
        }
      : {};

    return {
      userId: user?.id || null,
      conversationId: currentConversationId,
      messageId,
      projectId,
      processId: selectedProcessId || null,
      modelName: currentModel?.modelName || null,
      thinkingLevel: currentThinkingLevel || null,
      pageUrl: window.location.href,
      path: location.pathname,
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recentMessages: getRecentMessageSnippets(),
      lastServerMetadata: metadata || null,
      ...targetContext,
      consoleErrors: getRecentErrors()
    };
  };

  const openFeedbackModal = ({ messageId = null, rating = null, type = 'BUG' }) => {
    setFeedbackTarget({ messageId, rating, initialType: type });
    setShowFeedbackModal(true);
  };

  const handleSubmitFeedback = async (formData) => {
    const payload = {
      ...formData,
      conversationId: currentConversationId,
      messageId: feedbackTarget.messageId,
      projectId,
      isCrossProject: false,
      contextData: buildFeedbackContext(feedbackTarget.messageId)
    };

    await feedbackService.submitFeedback(payload);
  };

  const handleRateMessage = async (messageId, rating) => {
    if (ratedMessages[messageId] === rating) {
      return;
    }

    setRatedMessages((prev) => ({
      ...prev,
      [messageId]: rating
    }));

    if (rating < 0) {
      openFeedbackModal({ messageId, rating, type: 'BUG' });
      return;
    }

    try {
      await feedbackService.submitFeedback({
        type: 'PRAISE',
        title: 'Thumbs up',
        description: 'User rated this response as helpful.',
        rating,
        conversationId: currentConversationId,
        messageId,
        projectId,
        isCrossProject: false,
        contextData: buildFeedbackContext(messageId)
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const loadChatHistory = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await chatService.getChatHistory(projectId, false);
      
      if (response.data && response.data.length > 0) {
        setMessages(response.data);
      } else {
        // No history, show welcome message
        setMessages([
          { id: 1, message: "Hello! I'm your UiPath code assistant. Ask me anything about your project's workflows, activities, or structure!", sender: 'assistant', timestamp: new Date().toISOString() }
        ]);
      }
    } catch (err) {
      console.error('Error loading chat history:', err);
      setError('Failed to load chat history');
      setMessages([
        { id: 1, message: "Hello! I'm your UiPath code assistant. Ask me anything about your project!", sender: 'assistant', timestamp: new Date().toISOString() }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId);
      
      // Remove from list
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // If we deleted the current conversation, create a new one
      if (conversationId === currentConversationId) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isTyping) return;

    const streamController = new AbortController();
    activeStreamControllerRef.current = streamController;
    activeStreamTypeRef.current = 'chat';

    // Clear input and error immediately
    setInputMessage('');
    setError(null);
    setIsTyping(true);
    
    // Reset textarea height to default
    const textarea = document.querySelector('textarea[placeholder*="Ask about"]');
    if (textarea) {
      textarea.style.height = 'auto';
    }

    // Add user message optimistically
    const tempUserMsg = {
      id: `temp-user-${Date.now()}`,
      message,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    const tempAssistantId = `temp-assistant-${Date.now()}`;
    const tempAssistantMsg = {
      id: tempAssistantId,
      message: '',
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      thinkingSteps: []
    };
    setThinkingSteps([]);
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg]);

    const updateThinkingSteps = (updateFn) => {
      setThinkingSteps(prev => {
        const updated = updateFn(prev);
        setMessages(msgs => msgs.map(m =>
          m.id === tempAssistantId ? { ...m, thinkingSteps: updated } : m
        ));
        return updated;
      });
    };

    try {
      let streamCompleted = false;

      // Call RAG-powered chat API with conversationId and processId
      console.log('📤 Sending message with:', { projectId, conversationId: currentConversationId, processId: selectedProcessId, modelName: currentModel?.modelName || null });
      for await (const event of chatService.sendMessageStream(
        projectId,
        message,
        currentConversationId,
        false,
        selectedProcessId,
        null,
        currentModel?.modelName || null,
        currentThinkingLevel || null,
        { signal: streamController.signal }
      )) {
        if (event.type === 'status') {
          const label = event.tool ? getToolLabel(event.tool, event.status) : 'Thinking';
          setStreamingStatus({
            tool: event.tool || null,
            status: event.status || 'thinking',
            label,
            detail: event.detail || null
          });

          if (event.status === 'thinking') {
            updateThinkingSteps(prev => {
              const lastStep = prev[prev.length - 1];
              if (lastStep?.tool === 'thinking') {
                return prev;
              }
              const hasRunningThinking = prev.some(step => step.tool === 'thinking' && step.status === 'running');
              if (hasRunningThinking) {
                return prev;
              }
              return [...prev, { tool: 'thinking', label: 'Thinking', status: 'running', startedAt: Date.now() }];
            });
          }

          if (event.status === 'tool_start' && event.tool) {
            const stepLabel = toolLabelMap[event.tool] || event.tool;
            const detail = event.detail || null;
            const fullDetail = event.fullDetail || detail;
            updateThinkingSteps(prev => {
              const now = Date.now();
              const closedThinking = prev.map(step =>
                step.tool === 'thinking' && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt }
                  : step
              );
              return [...closedThinking, { tool: event.tool, label: stepLabel, detail, fullDetail, status: 'running', startedAt: now }];
            });
          }

          if (event.status === 'tool_end' && event.tool) {
            updateThinkingSteps(prev => {
              const now = Date.now();
              const updated = prev.map(step =>
                step.tool === event.tool && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt }
                  : step
              );
              return updated;
            });
          }

          if (event.status === 'tool_error' && event.tool) {
            updateThinkingSteps(prev => {
              const now = Date.now();
              return prev.map(step =>
                step.tool === event.tool && step.status === 'running'
                  ? {
                      ...step,
                      status: 'failed',
                      durationMs: now - step.startedAt,
                      detail: event.detail || step.detail,
                      fullDetail: event.fullDetail || event.detail || step.fullDetail || step.detail
                    }
                  : step
              );
            });
          }
        }

        if (event.type === 'token') {
          setMessages(prev => prev.map(m =>
            m.id === tempAssistantId
              ? { ...m, message: `${m.message || ''}${event.content || ''}` }
              : m
          ));
        }

        if (event.type === 'done') {
          streamCompleted = true;
          if (!currentConversationId && event.conversationId) {
            setCurrentConversationId(event.conversationId);
            setConversations(prev => [{
              id: event.conversationId,
              projectId,
              title: message.length > 50 ? message.substring(0, 47) + '...' : message,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isCrossProject: false,
              _count: { messages: 2 }
            }, ...prev]);
          }

          // Finalize steps and replace temp messages in one atomic setMessages call
          setMessages(prev => {
            const streamingMsg = prev.find(m => m.id === tempAssistantId);
            const rawSteps = streamingMsg?.thinkingSteps || thinkingSteps;
            const now = Date.now();
            const finalSteps = rawSteps.map(step =>
              step.status === 'running'
                ? { ...step, status: 'done', durationMs: now - step.startedAt }
                : step
            );
            const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId);
            const assistantWithSteps = finalSteps.length > 0
              ? { ...event.assistantMessage, thinkingSteps: finalSteps }
              : event.assistantMessage;
            return [...withoutTemp, event.userMessage, assistantWithSteps];
          });
          setThinkingSteps([]);

          if (event.metadata) {
            setMetadata(event.metadata);
          }

          if (event.conversationId) {
            setConversations(prev =>
              prev.map(c =>
                c.id === event.conversationId
                  ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
                  : c
              )
            );
          }
        }

        if (event.type === 'error') {
          throw new Error(event.message || 'Streaming error');
        }
      }

      if (!streamCompleted && streamController.signal.aborted) {
        throw new DOMException('Request aborted', 'AbortError');
      }
    } catch (err) {
      if (isAbortLikeError(err)) {
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId));
        setError(null);
        return;
      }

      console.error('Error sending message:', err);
      
      // Determine user-friendly error message
      let userErrorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (err.response?.status === 503) {
        userErrorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.status === 504) {
        userErrorMessage = 'The request timed out. Please try a simpler question or try again later.';
      } else if (err.response?.status === 500) {
        userErrorMessage = 'An internal server error occurred. Our team has been notified. Please try again later.';
      } else if (err.response?.data?.error) {
        userErrorMessage = err.response.data.error;
        if (err.response.data.details) {
          userErrorMessage += `\n\n${err.response.data.details}`;
        }
      } else if (err.message?.includes('Network Error')) {
        userErrorMessage = 'Network connection lost. Please check your internet connection and try again.';
      }
      
      // Show error message in chat
      const errorMsg = {
        id: `error-${Date.now()}`,
        message: `❌ **Error**: ${userErrorMessage}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId);
        return [...withoutTemp, errorMsg];
      });
      setError(`Failed to send message: ${err.response?.data?.error || err.message}`);
    } finally {
      if (activeStreamControllerRef.current === streamController) {
        activeStreamControllerRef.current = null;
        activeStreamTypeRef.current = null;
      }
      setIsTyping(false);
      setStreamingStatus(null);
      setThinkingSteps([]);
    }
  };

  const handleKeyPress = (e) => {
    // Plain Enter sends the message
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Ctrl+Enter or Shift+Enter creates a new line (allow default behavior)
  };

  const handleAnalyzeCodeClick = () => {
    // Start a new chat for code analysis
    handleNewChat();
    setShowAnalysisForm(true);
  };

  const handleCodeAnalysis = async (analysisRequest) => {
    const streamController = new AbortController();
    activeStreamControllerRef.current = streamController;
    activeStreamTypeRef.current = 'analysis';

    setIsAnalyzing(true);
    setIsTyping(true); // Show typing indicator during analysis
    setShowAnalysisForm(false);
    setError(null);

    let tempUserMsgId = null;
    let tempAssistantId = null;

    try {
      // Add user message about analysis request
      const analysisDescription =
        analysisRequest.scope === 'specific-files'
          ? [
              `Running code analysis for ${analysisRequest.files.length} selected file(s):`,
              ...analysisRequest.files.map((filePath) => `- ${filePath}`)
            ].join('\n')
          : `Running code analysis for ${
              analysisRequest.scope === 'full-project' ? 'full project' : 'selected process'
            }...`;

      const tempUserMsg = {
        id: `temp-user-${Date.now()}`,
        message: analysisDescription,
        sender: 'user',
        timestamp: new Date().toISOString()
      };
      tempUserMsgId = tempUserMsg.id;
      tempAssistantId = `temp-assistant-${Date.now()}`;
      const tempAssistantMsg = {
        id: tempAssistantId,
        message: '',
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isStreaming: true,
        thinkingSteps: []
      };

      setThinkingSteps([]);
      setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg]);

      const updateThinkingSteps = (updateFn) => {
        setThinkingSteps(prev => {
          const updated = updateFn(prev);
          setMessages(msgs => msgs.map(m =>
            m.id === tempAssistantId ? { ...m, thinkingSteps: updated } : m
          ));
          return updated;
        });
      };

      let streamCompleted = false;

      for await (const event of chatService.analyzeCodeStream(
        analysisRequest.projectId,
        analysisRequest.processId,
        analysisRequest.scope,
        analysisRequest.files,
        currentConversationId,
        analysisRequest.analysisMode || 'comprehensive',
        { signal: streamController.signal }
      )) {
        if (event.type === 'status') {
          const normalizedTool = event.tool ? normalizeAnalysisTool(event.tool) : null;
          const thinkingPhaseCount = thinkingSteps.filter(step => step.tool === 'thinking').length;
          const label = normalizedTool
            ? getToolLabel(normalizedTool, event.status)
            : (event.status === 'thinking'
              ? (thinkingPhaseCount === 0 ? 'Preparation' : (thinkingPhaseCount === 1 ? 'Independent code reviews' : 'Thinking'))
              : 'Thinking');
          setStreamingStatus({
            tool: normalizedTool || null,
            status: event.status || 'thinking',
            label
          });

          if (event.status === 'thinking') {
            updateThinkingSteps(prev => {
              // Skip if last step is already a running thinking step
              const lastStep = prev[prev.length - 1];
              if (lastStep?.tool === 'thinking' && lastStep?.status === 'running') {
                return prev;
              }

              // Determine which phase we're in based on phase tool steps present
              const hasPreparation = prev.some(step => step.tool === 'analysis_phase_preparation' || step.label === 'Preparation');
              const hasReview = prev.some(step => step.tool === 'analysis_phase_review' || step.label === 'Independent code reviews');
              const hasConsolidation = prev.some(step => step.tool === 'analysis_phase_consolidation');

              let nextThinkingLabel;
              let nextLevel;
              if (!hasPreparation) {
                nextThinkingLabel = 'Preparation';
                nextLevel = 'primary';
              } else if (!hasReview) {
                nextThinkingLabel = 'Independent code reviews';
                nextLevel = 'primary';
              } else {
                // During consolidation or later - sub-level thinking between tool calls
                nextThinkingLabel = 'Thinking';
                nextLevel = hasConsolidation ? 'sub' : 'primary';
              }

              return normalizeAnalysisThoughtChain([
                ...prev,
                { tool: 'thinking', label: nextThinkingLabel, status: 'running', startedAt: Date.now(), level: nextLevel }
              ]);
            });
          }

          if (event.status === 'tool_start' && event.tool) {
            const normalizedTool = normalizeAnalysisTool(event.tool);
            const stepLabel = toolLabelMap[normalizedTool] || normalizedTool;
            const stepLevel = event.level || (primaryTools.has(normalizedTool) ? 'primary' : 'sub');
            updateThinkingSteps(prev => {
              const now = Date.now();
              const closedThinking = prev.map(step =>
                step.tool === 'thinking' && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt }
                  : step
              );

              const shouldEnsureReviewParent = normalizedTool === 'analysis_reviewer_a' || normalizedTool === 'analysis_reviewer_b';
              const withReviewParent = shouldEnsureReviewParent && !closedThinking.some(step => step.tool === 'analysis_phase_review')
                ? [
                    ...closedThinking,
                    {
                      tool: 'analysis_phase_review',
                      label: toolLabelMap.analysis_phase_review,
                      level: 'primary',
                      status: 'running',
                      startedAt: now
                    }
                  ]
                : closedThinking;

              return normalizeAnalysisThoughtChain([
                ...withReviewParent,
                { tool: normalizedTool, label: stepLabel, detail: event.detail || null, fullDetail: event.fullDetail || event.detail || null, level: stepLevel, status: 'running', startedAt: now }
              ]);
            });
          }

          if (event.status === 'tool_end' && event.tool) {
            const normalizedTool = normalizeAnalysisTool(event.tool);
            updateThinkingSteps(prev => {
              const now = Date.now();
              return normalizeAnalysisThoughtChain(prev.map(step =>
                step.tool === normalizedTool && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt, detail: event.detail || step.detail, level: event.level || step.level }
                  : step
              ));
            });
          }

          if (event.status === 'tool_error' && event.tool) {
            const normalizedTool = normalizeAnalysisTool(event.tool);
            updateThinkingSteps(prev => {
              const now = Date.now();
              return normalizeAnalysisThoughtChain(prev.map(step =>
                step.tool === normalizedTool && step.status === 'running'
                  ? {
                      ...step,
                      status: 'failed',
                      durationMs: now - step.startedAt,
                      detail: event.detail || step.detail,
                      fullDetail: event.fullDetail || event.detail || step.fullDetail || step.detail,
                      level: event.level || step.level
                    }
                  : step
              ));
            });
          }
        }

        if (event.type === 'done') {
          streamCompleted = true;
          if (!currentConversationId && event.conversationId) {
            setCurrentConversationId(event.conversationId);
            const title = `Code Analysis: ${
              analysisRequest.scope === 'full-project' ? 'Full Project' :
              analysisRequest.scope === 'full-process' ? 'Process' :
              `${analysisRequest.files.length} File(s)`
            }`;

            setConversations(prev => [{
              id: event.conversationId,
              projectId,
              title,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isCrossProject: false,
              _count: { messages: 2 }
            }, ...prev]);
          }

          setMessages(prev => {
            const streamingMsg = prev.find(m => m.id === tempAssistantId);
            const rawSteps = streamingMsg?.thinkingSteps || thinkingSteps;
            const now = Date.now();
            const finalizedStreamSteps = normalizeAnalysisThoughtChain(rawSteps.map(step =>
              step.status === 'running'
                ? { ...step, status: 'done', durationMs: now - step.startedAt }
                : step
            ));

            // Prefer streaming steps (they capture ALL events: preparation + review + consolidation + thinking).
            // Server-side thought chain only covers V2 steps (review, consolidation) and misses preparation phase.
            const serverSteps = event.assistantMessage?.thinkingSteps || event.metadata?.thoughtChain || [];
            const finalSteps = finalizedStreamSteps.length > 0
              ? finalizedStreamSteps
              : (serverSteps.length > 0 ? normalizeAnalysisThoughtChain(serverSteps) : []);

            const withoutTemp = prev.filter(m => m.id !== tempUserMsgId && m.id !== tempAssistantId);
            const assistantWithSteps = finalSteps.length > 0
              ? { ...event.assistantMessage, thinkingSteps: finalSteps }
              : event.assistantMessage;

            return [...withoutTemp, event.userMessage, assistantWithSteps];
          });

          setThinkingSteps([]);

          if (event.metadata) {
            setMetadata(event.metadata);
          }

          if (event.conversationId) {
            setConversations(prev =>
              prev.map(c =>
                c.id === event.conversationId
                  ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
                  : c
              )
            );
          }
        }

        if (event.type === 'error') {
          throw new Error(event.message || 'Streaming error');
        }
      }

      if (!streamCompleted && streamController.signal.aborted) {
        throw new DOMException('Request aborted', 'AbortError');
      }
      
      // Reload processes in case new processes were created during analysis
      loadProcesses();
    } catch (err) {
      if (isAbortLikeError(err)) {
        setMessages(prev => prev.filter(m => m.id !== tempUserMsgId && m.id !== tempAssistantId));
        setError(null);
        return;
      }

      console.error('Error running code analysis:', err);

      const errorMsg = {
        id: `error-${Date.now()}`,
        message: err.response?.data?.error || 'Code analysis failed. Please try again.',
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsgId && m.id !== tempAssistantId);
        return [...withoutTemp, errorMsg];
      });
      setError('Code analysis failed');
    } finally {
      if (activeStreamControllerRef.current === streamController) {
        activeStreamControllerRef.current = null;
        activeStreamTypeRef.current = null;
      }
      setIsAnalyzing(false);
      setIsTyping(false); // Hide typing indicator
      setStreamingStatus(null);
      setThinkingSteps([]);
    }
  };

  const clearHistory = async () => {
    setShowClearHistoryModal(true);
  };

  const handleConfirmClearHistory = async () => {
    try {
      await chatService.clearChatHistory(projectId, false);
      setConversations([]);
      setMessages([]);
      setCurrentConversationId(null);
      setMetadata(null);
      setError(null);
      setShowClearHistoryModal(false);
    } catch (err) {
      console.error('Error clearing history:', err);
      setError('Failed to clear chat history');
      setShowClearHistoryModal(false);
    }
  };

  const handleCancelClearHistory = () => {
    setShowClearHistoryModal(false);
  };

  const handleModelChange = (modelName) => {
    const selectedModel = buildModelSelection(availableModels, modelName);
    if (!selectedModel) {
      return;
    }

    setCurrentModel(selectedModel);
    setCurrentThinkingLevel((prev) => {
      const normalized = normalizeThinkingLevel(prev);
      if (normalized === 'minimal' && !isMinimalSupported(selectedModel.modelName)) {
        return 'low';
      }
      return normalized;
    });
    setIsCustomSelection(true);
  };

  const handleThinkingLevelChange = (thinkingLevel) => {
    const normalized = normalizeThinkingLevel(thinkingLevel);
    if (normalized === 'minimal' && !isMinimalSupported(currentModel?.modelName)) {
      setCurrentThinkingLevel('low');
      return;
    }

    setCurrentThinkingLevel(normalized);
    setIsCustomSelection(true);
  };

  const handleSliderChange = (nextSliderValue) => {
    applySliderPreset(nextSliderValue, sliderPresets, availableModels);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Strip markdown formatting from text
  const stripMarkdown = (text) => {
    let cleaned = text;
    
    // Remove bold (**text** or __text__)
    cleaned = cleaned.replace(/\*\*([^*]+?)\*\*/g, '$1');
    cleaned = cleaned.replace(/__([^_]+?)__/g, '$1');
    
    // Remove italic (*text* or _text_)
    cleaned = cleaned.replace(/\*([^*]+?)\*/g, '$1');
    cleaned = cleaned.replace(/_([^_]+?)_/g, '$1');
    
    // Remove code blocks (```code```)
    cleaned = cleaned.replace(/```[\s\S]*?```/g, (match) => {
      // Extract code content without the backticks and language identifier
      return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
    });
    
    // Remove inline code (`code`)
    cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
    
    // Remove headers (# ## ###)
    cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
    
    // Remove strikethrough (~~text~~)
    cleaned = cleaned.replace(/~~([^~]+?)~~/g, '$1');
    
    // Remove links but keep text [text](url) -> text
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove images ![alt](url) -> alt
    cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
    
    // Remove horizontal rules (---, ___, ***)
    cleaned = cleaned.replace(/^[\-_*]{3,}\s*$/gm, '');
    
    // Remove blockquotes (>)
    cleaned = cleaned.replace(/^>\s+/gm, '');
    
    // Clean up extra whitespace
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    return cleaned.trim();
  };

  // Copy message to clipboard
  const handleCopyMessage = async (messageId, content) => {
    try {
      // Strip markdown formatting before copying
      const plainText = stripMarkdown(content);
      await navigator.clipboard.writeText(plainText);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy message');
    }
  };

  // Export message to Google Docs
  const handleExportToGoogleDocs = async (messageId, content) => {
    try {
      setExportingMessageId(messageId);
      const response = await chatService.exportMessageToGoogleDocs(messageId, content, projectName);
      
      if (response.data && response.data.documentUrl) {
        const { documentUrl, title } = response.data;
        setExportedDocument({ url: documentUrl, title });
        setShowExportModal(true);
      }
    } catch (err) {
      console.error('Error exporting to Google Docs:', err);
      
      // Check if we need to request Google Docs permissions
      if (err.response?.data?.needsAuth) {
        const currentPath = location.pathname;
        const authUrl = resolveGoogleDocsAuthUrl(currentPath);
        setGoogleDocsAuthUrl(authUrl);
        setShowGoogleDocsPermissionModal(true);
        return;
      }
      
      // Handle other errors
      let errorMessage = 'Failed to export to Google Docs. ';
      if (err.response?.status === 401) {
        errorMessage += 'Please log in with Google to use this feature.';
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      }
      alert(errorMessage);
    } finally {
      setExportingMessageId(null);
    }
  };

  const handleConfirmGoogleDocsPermission = () => {
    window.location.href = googleDocsAuthUrl;
  };

  const handleCancelGoogleDocsPermission = () => {
    setShowGoogleDocsPermissionModal(false);
    setGoogleDocsAuthUrl('');
  };

  const suggestedQuestions = [
    "What does this project do?",
    "What applications does it integrate with?",
    "Is it using the ReFramework?",
    "Is the 'Process workflow' following TQA's best practices?",
    "What Errors did the workflow analyzer find?",
  ];

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return !inline && language ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="rounded-lg my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <div className="flex gap-0 h-full w-full overflow-hidden">
      {/* Main Chat Area - Full width */}
      <div className="flex-1 flex flex-col rounded-none border-0 overflow-hidden px-4 pt-4 pb-4 relative" style={{background: 'var(--tqa-black)', borderRight: '1px solid var(--tqa-border-dark)'}}>
      
        {/* Loading Overlay - Only on chat area */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10" style={{background: 'rgba(34,34,34,0.85)'}}>
            <div style={{color: 'rgba(255,255,255,0.5)'}}>Loading...</div>
          </div>
        )}
      
        {/* Scrollable Chat Messages Area */}
        <div ref={chatContainerRef} className="flex-1 h-full overflow-y-auto mb-4">
          <div
            className="flex flex-col h-full space-y-3 px-2"
          >
          {messages.length === 0 ? (
            <div className="flex items-end justify-center h-full text-center px-4 pb-8">
              <div className="max-w-2xl">
                <p className="font-medium mb-2" style={{color: 'rgba(255,255,255,0.85)'}}>
                  {projectName ? `${projectName} Chat` : 'Project Chat'}
                </p>
                <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.5)'}}>
                  Ask questions about workflows, activities, structure, or issues in this project
                </p>
                <div className="mb-4">
                  <p className="text-sm mb-3" style={{color: 'rgba(255,255,255,0.5)'}}>Try asking:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {suggestedQuestions.map((question, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInputMessage(question)}
                        className="text-xs px-3 py-1.5 rounded-full transition-colors"
                        style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.65)'}}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(130,255,207,0.1)'; e.currentTarget.style.borderColor = 'rgba(130,255,207,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--tqa-surface-2)'; e.currentTarget.style.borderColor = 'var(--tqa-border-dark)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className="max-w-[85%] rounded-lg px-4 py-3"
                  style={
                    message.sender === 'user'
                      ? {background: 'rgba(130,255,207,0.15)', border: '1px solid rgba(130,255,207,0.35)', color: 'rgba(255,255,255,0.9)'}
                      : message.isError
                      ? {background: 'rgba(230,100,97,0.12)', border: '1px solid rgba(230,100,97,0.4)', color: 'rgba(255,255,255,0.85)'}
                      : message.isStreaming
                      ? {background: 'var(--tqa-surface-2)', color: 'rgba(255,255,255,0.85)'}
                      : {background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}
                  }
                >
                  {message.sender === 'assistant' && !message.isError ? (
                    <div className="chat-markdown">
                    {/* Collapsible thinking steps */}
                    <ThoughtChain 
                      steps={message.thinkingSteps} 
                      isStreaming={message.isStreaming}
                      isToolCallStep={isToolCallStep}
                      isThinkingStep={isThinkingStep}
                    />
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {message.message || message.text}
                    </ReactMarkdown>
                    
                    {/* Display LLM Execution Metrics and Action Buttons */}
                    {message.llmExecution && (
                      <div className="mt-3 pt-3" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
                        <div className="flex items-center justify-between gap-4">
                          {/* Left side: Metrics */}
                          <div className="flex items-center gap-3 text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>
                            {message.timestamp && (
                              <span style={{color: 'rgba(255,255,255,0.4)'}}>
                                {formatTimestamp(message.timestamp)}
                              </span>
                            )}
                            <span style={{color: 'rgba(255,255,255,0.65)'}}>
                              🤖 {message.llmExecution.initialAnalysisModelDisplayName || message.llmExecution.initialAnalysisModelName || message.llmExecution.displayName || message.llmExecution.modelName || 'Unknown model'} ({String(message.llmExecution.thinkingLevel || 'low').toLowerCase()})
                            </span>
                            <span style={{color: 'var(--tqa-green)'}}>
                              ⏱️ {(message.llmExecution.durationMs / 1000).toFixed(2)}s
                            </span>
                            <span className="font-medium text-green-700">
                              💰 ${parseFloat(message.llmExecution.estimatedCost).toFixed(3)}
                              {(() => {
                                // Calculate cumulative cost up to and including this message
                                const messageIndex = messages.findIndex(m => m.id === message.id);
                                const cumulativeCost = messages
                                  .slice(0, messageIndex + 1)
                                  .filter(m => m.llmExecution && m.llmExecution.estimatedCost)
                                  .reduce((sum, m) => sum + parseFloat(m.llmExecution.estimatedCost), 0);
                                return ` (total: $${cumulativeCost.toFixed(3)})`;
                              })()}
                            </span>
                          </div>
                          
                          {/* Right side: Action buttons */}
                          {!message.isStreaming && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopyMessage(message.id, message.message || message.text)}
                              className="transition-colors p-1.5 rounded"
                              style={{color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--tqa-green)'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
                              title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                            >
                              {copiedMessageId === message.id ? (
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleExportToGoogleDocs(message.id, message.message || message.text)}
                              disabled={exportingMessageId === message.id}
                              className="transition-colors p-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--tqa-green)'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
                              title="Export to Google Docs"
                            >
                              {exportingMessageId === message.id ? (
                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                  <path d="M14 2v6h6" fill="#A1C2FA"/>
                                  <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleRateMessage(message.id, 1)}
                              className="transition-colors p-1.5 rounded"
                              style={ratedMessages[message.id] === 1 ? {color: '#82FFCF', background: 'rgba(130,255,207,0.12)'} : {color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => { if (ratedMessages[message.id] !== 1) { e.currentTarget.style.color = '#82FFCF'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; } }}
                              onMouseLeave={e => { if (ratedMessages[message.id] !== 1) { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; } }}
                              title="Thumbs up"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 10c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1v-7zm5-1.1l3.4-5.2c.2-.4.6-.7 1-.7h.6c1 0 1.7.9 1.5 1.9l-.6 3h4.4c1.2 0 2.1 1.1 1.9 2.2l-1.4 6.3c-.2.9-1 1.5-1.9 1.5H7V8.9z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRateMessage(message.id, -1)}
                              className="transition-colors p-1.5 rounded"
                              style={ratedMessages[message.id] === -1 ? {color: 'var(--tqa-red)', background: 'rgba(230,100,97,0.12)'} : {color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => { if (ratedMessages[message.id] !== -1) { e.currentTarget.style.color = 'var(--tqa-red)'; e.currentTarget.style.background = 'rgba(230,100,97,0.08)'; } }}
                              onMouseLeave={e => { if (ratedMessages[message.id] !== -1) { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; } }}
                              title="Thumbs down"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1V3zm5 8.1l3.4 5.2c.2.4.6.7 1 .7h.6c1 0 1.7-.9 1.5-1.9l-.6-3h4.4c1.2 0 2.1-1.1 1.9-2.2l-1.4-6.3c-.2-.9-1-1.5-1.9-1.5H7v9.1z" />
                              </svg>
                            </button>
                          </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Icons for messages without metrics */}
                    {!message.llmExecution && !message.isStreaming && (
                      <div className="mt-3 pt-2 flex items-center justify-end gap-2" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
                        <button
                          onClick={() => handleCopyMessage(message.id, message.message || message.text)}
                          className="transition-colors p-1.5 rounded"
                          style={{color: 'rgba(255,255,255,0.4)'}}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--tqa-green)'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
                          title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                        >
                          {copiedMessageId === message.id ? (
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleExportToGoogleDocs(message.id, message.message || message.text)}
                          disabled={exportingMessageId === message.id}
                          className="transition-colors p-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{color: 'rgba(255,255,255,0.4)'}}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--tqa-green)'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
                          title="Export to Google Docs"
                        >
                          {exportingMessageId === message.id ? (
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                              <path d="M14 2v6h6" fill="#A1C2FA"/>
                              <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleRateMessage(message.id, 1)}
                          className="transition-colors p-1.5 rounded"
                          style={ratedMessages[message.id] === 1 ? {color: '#82FFCF', background: 'rgba(130,255,207,0.12)'} : {color: 'rgba(255,255,255,0.4)'}}
                          onMouseEnter={e => { if (ratedMessages[message.id] !== 1) { e.currentTarget.style.color = '#82FFCF'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; } }}
                          onMouseLeave={e => { if (ratedMessages[message.id] !== 1) { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; } }}
                          title="Thumbs up"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 10c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1v-7zm5-1.1l3.4-5.2c.2-.4.6-.7 1-.7h.6c1 0 1.7.9 1.5 1.9l-.6 3h4.4c1.2 0 2.1 1.1 1.9 2.2l-1.4 6.3c-.2.9-1 1.5-1.9 1.5H7V8.9z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRateMessage(message.id, -1)}
                          className="transition-colors p-1.5 rounded"
                          style={ratedMessages[message.id] === -1 ? {color: 'var(--tqa-red)', background: 'rgba(230,100,97,0.12)'} : {color: 'rgba(255,255,255,0.4)'}}
                          onMouseEnter={e => { if (ratedMessages[message.id] !== -1) { e.currentTarget.style.color = 'var(--tqa-red)'; e.currentTarget.style.background = 'rgba(230,100,97,0.08)'; } }}
                          onMouseLeave={e => { if (ratedMessages[message.id] !== -1) { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; } }}
                          title="Thumbs down"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1V3zm5 8.1l3.4 5.2c.2.4.6.7 1 .7h.6c1 0 1.7-.9 1.5-1.9l-.6-3h4.4c1.2 0 2.1-1.1 1.9-2.2l-1.4-6.3c-.2-.9-1-1.5-1.9-1.5H7v9.1z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {message.message || message.text}
                  </div>
                )}
                {message.timestamp && !message.llmExecution && !message.isStreaming && (
                  <div className="text-xs mt-2" style={{color: message.sender === 'user' ? 'rgba(130,255,207,0.7)' : 'rgba(255,255,255,0.4)'}}>
                    {formatTimestamp(message.timestamp)}
                  </div>
                )}
              </div>
            </div>
            ))
          )}
          
        </div>
      </div>

      {/* Fixed Bottom Section */}
      <div className="flex-shrink-0">
        {/* Metadata Display (optional) */}
        {metadata && metadata.retrievedDocuments && metadata.retrievedDocuments.length > 0 && (
          <div className="mb-4 px-2">
            <details className="rounded-lg p-3" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
              <summary className="text-sm cursor-pointer font-medium" style={{color: 'rgba(255,255,255,0.75)'}}>
                📚 Referenced {metadata.documentsRetrieved} file{metadata.documentsRetrieved !== 1 ? 's' : ''} 
                {metadata.processingTime && ` (${metadata.processingTime.toFixed(1)}s)`}
              </summary>
              <ul className="mt-2 space-y-1 text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>
                {metadata.retrievedDocuments.map((doc, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="truncate flex-1">{doc.filePath}</span>
                    <span className="ml-2" style={{color: 'var(--tqa-green)'}}>
                      {(doc.similarity * 100).toFixed(0)}% match
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-4 px-2">
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          </div>
        )}

        {/* Code Analysis Form */}
        {showAnalysisForm && (
          <div>
            <CodeAnalysisForm
              projectId={projectId}
              onSubmit={handleCodeAnalysis}
              onStop={stopActiveResponse}
              onCancel={() => setShowAnalysisForm(false)}
              isAnalyzing={isAnalyzing}
            />
          </div>
        )}
        
        {/* Input Area - Hidden when analysis form is shown */}
        {!showAnalysisForm && (
          <div className="space-y-3">
            {/* AI Disclaimer + Feedback */}
            <div className="px-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs italic" style={{color: 'rgba(255,255,255,0.4)'}}>
                ⚠️ Responses are AI-generated and may contain errors. Use as reference only.
              </p>
              <button
                type="button"
                onClick={() => openFeedbackModal({ type: 'IDEA' })}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors"
                style={{color: 'rgba(255,255,255,0.5)', border: '1px solid var(--tqa-border-dark)'}}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--tqa-green)'; e.currentTarget.style.borderColor = 'rgba(130,255,207,0.4)'; e.currentTarget.style.background = 'rgba(130,255,207,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor = 'var(--tqa-border-dark)'; e.currentTarget.style.background = 'transparent'; }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20l9-8-9-8-9 8 9 8z" />
                </svg>
                Feedback
              </button>
            </div>
            
            {/* Process Selector - Only show when there are multiple processes */}
            {processes.length > 1 && (
              <div className="px-2">
                <ProcessSelector
                  processes={processes}
                  selectedProcessId={selectedProcessId}
                  onProcessChange={handleProcessChange}
                  isLoading={isLoadingProcesses}
                />
              </div>
            )}

            
            
            {/* Input Box */}
            <div className="px-2 flex space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={selectedProcessId 
                  ? `Ask about this process's workflows, activities, or structure... (Ctrl+Enter for new line)` 
                  : `Ask about workflows, activities, or project structure... (Ctrl+Enter for new line)`}
                className="flex-grow rounded-lg px-4 py-2 focus:outline-none resize-none min-h-[42px] max-h-[200px]"
                disabled={isTyping || isAnalyzing}
                rows={1}
                style={{
                  background: 'var(--tqa-surface-2)',
                  border: '1px solid var(--tqa-border-dark)',
                  color: 'rgba(255,255,255,0.85)',
                  height: 'auto',
                  overflow: 'auto'
                }}
                onInput={(e) => {
                  // Auto-resize textarea based on content
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
              />
              <button
                onClick={isTyping ? stopActiveResponse : sendMessage}
                disabled={isTyping ? false : (!inputMessage.trim() || isAnalyzing)}
                className={`px-4 py-2 self-end ${
                  isTyping
                    ? 'bg-red-600 text-white rounded-md hover:bg-red-700'
                    : 'btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {isTyping ? 'Stop' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
      
      {/* Chat History Sidebar - Fixed width */}
      <div className="w-80 h-full">
        <ChatHistorySidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={loadConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          onAnalyzeCode={handleAnalyzeCodeClick}
          onStopAnalyze={stopActiveResponse}
          isAnalyzing={isAnalyzing || activeStreamTypeRef.current === 'analysis'}
          isLoading={isLoadingConversations}
          availableModels={availableModels}
          currentModel={currentModel}
          currentThinkingLevel={currentThinkingLevel}
          sliderValue={sliderValue}
          isCustomSelection={isCustomSelection}
          onModelChange={handleModelChange}
          onThinkingLevelChange={handleThinkingLevelChange}
          onSliderChange={handleSliderChange}
          showResponseMode={!showAnalysisForm}
        />
      </div>

      {/* Google Docs Export Success Modal */}
      <GoogleDocsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        documentUrl={exportedDocument.url}
        title={exportedDocument.title}
      />

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleSubmitFeedback}
        initialType={feedbackTarget.initialType}
        initialRating={feedbackTarget.rating}
      />

      {/* Clear History Confirmation Modal */}
      {showClearHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg shadow-xl p-6 w-96" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
            <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.85)'}}>Delete All Conversations</h3>
            <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.65)'}}>
              Are you sure you want to delete all conversations? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelClearHistory}
                className="btn-secondary px-4 py-2 text-sm font-medium rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClearHistory}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Docs Permission Request Modal */}
      {showGoogleDocsPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg shadow-xl p-6 w-96" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
            <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.85)'}}>Google Docs Permission Required</h3>
            <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.65)'}}>
              Google Docs permissions are required to export chat responses.
              <br /><br />
              Click <strong>Grant Permission</strong> to allow this app to create and access Google Docs on your behalf.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelGoogleDocsPermission}
                className="btn-secondary px-4 py-2 text-sm font-medium rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmGoogleDocsPermission}
                className="btn-primary px-4 py-2 text-sm font-medium rounded-lg"
              >
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTab;